import React, { FC, FormEvent, ReactNode, useEffect, useState } from 'react';
import * as DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import cls from 'classnames';

import { isMobileApp } from 'src/explore/services/mobile';
import { useShopConfig } from 'src/explore/hooks/useShopConfig';
import LoginErrorParser from 'src/explore/services/api/ErrorParsers/Login';
import SignUpErrorParser from 'src/explore/services/api/ErrorParsers/SignUp';

import { useRoutes } from 'src/explore/hooks/useRoutes';

import ForgotPassword from 'src/explore/compositions/ForgotPassword';
import { Modal } from 'src/shoppe/elements/Modal';

import * as authDataActionCreators from 'src/explore/data/auth/actions';
import * as componentActionCreators from './actions';

import { Login } from './scenes/Login';
import { Signup } from './scenes/Signup';
import { Title, TitleProps } from './elements/Title';
import { Logo } from './elements/Logo';

import styles from './index.module.sass';
import { isValidUrl } from './services';

interface Props {
  closeBtn?: boolean;
  CustomTitle?: FC<TitleProps>;
  header?: ReactNode;
  refresh?: boolean;
}

export const Auth: FC<Props> = ({ closeBtn = true, CustomTitle, header, refresh = false }) => {
  const emailFromOpen = useSelector(
    ( state: { Auth: { defaultEmail: string | null } }) => state.Auth.defaultEmail
  );
  const loggedIn = useSelector(( state: { data: { account: any } }) => state.data.account );
  const mode = useSelector(( state: { Auth: { mode: string } }) => state.Auth.mode );
  const redirectTo = useSelector(
    ( state: { Auth: { redirectTo: string | null } }) => state.Auth.redirectTo
  );
  const visible = useSelector(( state: { Auth: { visible: boolean } }) => state.Auth.visible );
  const config = useShopConfig();

  const [ account, setAccount ] = useState<boolean>( false );
  const [ defaultEmail, setDefaultEmail ] = useState<string>( '' );
  const [ loading, setLoading ] = useState<boolean>( false );
  const [ loginError, setLoginError ] = useState<string[] | null>( null );
  const [ signUpError, setSignUpError ] = useState<string[] | null>( null );

  const routes = useRoutes();

  const dispatch = useDispatch();

  const resetState = () => {
    setLoginError( null );
    setSignUpError( null );
    setAccount( false );
  };

  const switchMode = ( newMode: string ) => {
    resetState();
    dispatch( componentActionCreators.switchMode( newMode ));
  };

  const handleSuccessfulAuth = ( response: any ) => {
    if ( response ) {
      if ( redirectTo ) {
        if ( isMobileApp()) {
          dispatch( componentActionCreators.close());
          ( window as any ).Turbolinks.visit( redirectTo );
        } else {
          ( window as any ).location = redirectTo;
        }
      } else if ( refresh ) {
        window.location.reload();
      } else {
        dispatch( componentActionCreators.close());
      }
    }
  };

  const handleSignUp = ( form: any ) => {
    // invalid
    if ( Object.keys( form.values ).some(( value: string ) => !form.validity[value])) {
      const errors = Object.keys( form.errors );
      if ( errors.length !== 0 ) {
        setSignUpError( errors.map(( error: string ) => form.errors[error]));
      } else {
        setSignUpError([
          "Whoops! Something went wrong and we can't create your account at this moment. Please try again later.",
        ]);
      }
    } else if ( form.values.password !== form.values.confirmation ) {
      setSignUpError([ 'Oops! The passwords do not match. Please try again.' ]);
    }

    // valid
    else {
      setLoading( true );

      dispatch<any>(
        authDataActionCreators.signUp({
          email_auth: { email: form.values.email, password: form.values.password },
        })
      )
        .then( handleSuccessfulAuth )
        .catch(( apiError: any ) => {
          setSignUpError([ SignUpErrorParser.parse( apiError ) ]);
        })
        .finally(() => setLoading( false ));
    }
  };

  const handleLogin = ( event: FormEvent, form: any ) => {
    event.preventDefault();

    // invalid
    if ( Object.keys( form.values ).some(( value: string ) => !form.validity[value])) {
      const errors = Object.keys( form.errors );
      if ( errors.length !== 0 ) {
        setLoginError( errors.map(( error: string ) => form.errors[error]));
      } else {
        setLoginError([
          "Whoops! Something went wrong and we can't log you in at this moment. Please try again later.",
        ]);
      }
    }

    // valid
    else {
      setLoading( true );

      dispatch<any>( authDataActionCreators.login({ email_auth: form.values }))
        .then( handleSuccessfulAuth )
        .catch(( apiError: any ) => {
          setLoginError([ LoginErrorParser.parse( apiError ) ]);
        })
        .finally(() => setLoading( false ));
    }
  };

  const handleClose = () => {
    dispatch( componentActionCreators.close());
    setTimeout( resetState, 200 );
  };

  const handleBackToLogin = () => {
    // order success pages handle login on page
    if ( /\/success\/?$/.test( window.location.pathname )) {
      handleClose();
    } else {
      switchMode( 'login' );
    }
  };

  useEffect(() => {
    const encodedEmail = new URLSearchParams( window.location.search ).get( 'ro' );
    if ( !!encodedEmail && typeof encodedEmail === 'string' ) {
      const decodedEmail = ( window as any ).atob( encodedEmail );
      setDefaultEmail( DOMPurify.sanitize( decodedEmail, {}));
    }
  }, []);

  useEffect(() => {
    if ( emailFromOpen ) {
      setDefaultEmail( emailFromOpen );
    }
  }, [ emailFromOpen ]);

  useEffect(() => {
    const mobileAppAuth = () => {
      if ( loggedIn.data ) {
        ( window as any ).Turbolinks.visit( routes.account.root());
      } else if ( visible ) {
        handleClose();
      } else {
        dispatch( componentActionCreators.open());
      }
    };

    // double listeners while we transition away from jQuery
    document.addEventListener( 'toggle.auth', mobileAppAuth );
    $( document ).on( 'toggle.auth', mobileAppAuth );
    return () => {
      document.removeEventListener( 'toggle.auth', mobileAppAuth );
      $( document ).off( 'toggle.auth', mobileAppAuth );
    };
  }, [ visible ]);

  return (
    <Modal
      closeBtn={closeBtn}
      containerClass={cls([
        styles.modal,
        'mw-100 p-0 border-0 mx-auto bg-transparent text-center spec__auth-container',
      ])}
      open={visible}
      onClick={handleClose}
      overlayClass="d-block d-sm-flex spec__auth-modal auth-modal"
    >
      {header}

      <div
        className={cls([
          styles.container,
          'position-relative px-6 pb-8 pb-sm-6 rounded auth-modal-content',
          isValidUrl( config?.merchantAuthLogo ) ? styles.usesImage : '',
        ])}
      >
        <Logo logoUrl={config?.merchantAuthLogo} />

        {CustomTitle ? (
          <CustomTitle account={account} mode={mode} />
        ) : (
          <Title account={account} mode={mode} />
        )}

        <div className="mt-2">
          { config?.rewardsPointsEnabled ? (
            <>Earn reward points, track your shipments, and make re-ordering quick and easy.</>
          ) : (
            <>Track your shipments, save addresses, and make re-ordering quick and easy.</>
          )}
        </div>

        <div className="mt-8">
          {mode === 'signup' && (
            <Signup
              defaultEmail={defaultEmail}
              loading={loading}
              signUpError={signUpError}
              switchMode={switchMode}
              onSubmit={handleSignUp}
            />
          )}

          {mode === 'login' && (
            <Login
              account={account}
              config={config}
              defaultEmail={defaultEmail}
              loading={loading}
              loginError={loginError}
              setAccount={setAccount}
              setDefaultEmail={setDefaultEmail}
              setLoading={setLoading}
              setLoginError={setLoginError}
              switchMode={switchMode}
              onSubmit={handleLogin}
              onForgotPasswordClicked={() => switchMode( 'forgot-password' )}
            />
          )}

          {mode === 'forgot-password' && (
            <ForgotPassword
              btnClass="btn-primary"
              defaultEmail={defaultEmail}
              onBack={handleBackToLogin}
              onClose={() => {
                switchMode( 'login' );
                handleClose();
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
